import type { TermsDocument } from '../types';

export const attendeeBookingCancellationRefundTerms: TermsDocument = {
  version: 1,
  lastUpdated: '8 April 2026',
  title: 'Careerbook Attendee Booking, Cancellation and Refund Terms',
  sections: [
    {
      title: 'Important notice',
      content: [
        {
          type: 'text',
          text: "These Attendee Booking, Cancellation and Refund Terms apply to Attendees who browse, book, buy, register for, or attend Events through Careerbook. They explain who you contract with, how payments and refunds work, what happens if you cancel, what happens if a Host cancels, and Careerbook's role as the platform provider. By purchasing or registering for an Event through Careerbook, you agree to these Terms.",
        },
      ],
    },
    {
      title: '1. Who you are contracting with',
      content: [
        {
          type: 'text',
          text: "1.1 Careerbook's platform role\nCareerbook Enterprise Ltd (Careerbook, we, us, or our) provides the platform through which Hosts can list, manage, and sell access to Events.",
        },
        {
          type: 'text',
          text: '1.2 Contract for the Event\nUnless Careerbook expressly states otherwise for a specific Event, the contract for the Event itself is between:',
        },
        {
          type: 'bulleted',
          items: ['you, as the Attendee; and', 'the relevant Host.'],
        },
        {
          type: 'text',
          text: 'The Host is responsible for the Event, the event description, the Refund Policy, and event delivery.',
        },
        {
          type: 'text',
          text: '1.3 Separate platform contract\nYour use of Careerbook is also subject to the Careerbook Platform Terms of Service.',
        },
      ],
    },
    {
      title: '2. Definitions',
      content: [
        { type: 'text', text: 'In these Terms:' },
        {
          type: 'bulleted',
          items: [
            'Attendee means a person who books, buys, registers for, accesses, or attends an Event.',
            'Booking Fee means the Careerbook platform fee charged in relation to a Paid Booking.',
            'Host means the person or organisation creating, promoting, managing, or delivering the Event.',
            'Paid Booking means a paid ticket, place, registration, or paid access right purchased through Careerbook.',
            'Paid Event means an Event sold for payment through Careerbook.',
            'Refund Policy means the cancellation and refund rules set by the Host for the Event.',
          ],
        },
      ],
    },
    {
      title: '3. Before you book',
      content: [
        {
          type: 'text',
          text: 'Before making a booking, you should review the Event details carefully, including:',
        },
        {
          type: 'bulleted',
          items: [
            'the Event title and description;',
            'the date and time;',
            'whether the Event is live online or in person;',
            'the organiser or Host;',
            'any eligibility, joining, attendance, or access requirements;',
            "the Host's Refund Policy;",
            'the total price, including any Booking Fee shown at checkout.',
          ],
        },
        {
          type: 'text',
          text: 'If you have any questions about the Event itself, you should contact the Host before booking where possible.',
        },
      ],
    },
    {
      title: '4. Prices and payment',
      content: [
        {
          type: 'text',
          text: '4.1 Total price\nThe amount you pay for a Paid Booking may include:',
        },
        {
          type: 'bulleted',
          items: [
            'the ticket, course, or registration fee set by the Host;',
            'the Careerbook Booking Fee;',
            'payment processing charges where shown;',
            'taxes where applicable.',
          ],
        },
        {
          type: 'text',
          text: '4.2 Payment authorisation\nBy completing a booking, you authorise the relevant payment to be charged using the payment method you provide.',
        },
        {
          type: 'text',
          text: '4.3 Booking confirmation\nA booking is confirmed once the payment has been successfully processed and Careerbook or the Host has issued confirmation.',
        },
      ],
    },
    {
      title: '5. Your cancellation rights',
      content: [
        {
          type: 'text',
          text: "5.1 Host-set refund policy applies\nEach Host sets its own Refund Policy for Paid Events. If you want to cancel a booking, the Host's Refund Policy will generally determine whether you are entitled to a refund.",
        },
        {
          type: 'text',
          text: "5.2 If you cancel within the Host's eligible refund window\nIf you cancel within the period and conditions stated in the Host's Refund Policy and your booking qualifies for a refund:",
        },
        {
          type: 'bulleted',
          items: [
            'the full ticket price, course fee, or other Event price paid to the Host will be refunded; and',
            'the Careerbook Booking Fee will ordinarily not be refunded.',
          ],
        },
        {
          type: 'text',
          text: 'This is subject always to any non-excludable legal rights or any contrary statement expressly made at checkout or by Careerbook in writing.',
        },
        {
          type: 'text',
          text: "5.3 If you cancel outside the Host's refund window\nIf you cancel outside the Host's stated refund window, you may receive no refund unless:",
        },
        {
          type: 'bulleted',
          items: ['the Host chooses to make an exception; or', 'applicable law requires a refund or other remedy.'],
        },
        {
          type: 'text',
          text: '5.4 Consumer law note\nMany Events on Careerbook are leisure or training services supplied on a specific date or within a specific period. In those cases, the usual 14-day cooling-off cancellation right for distance contracts may not apply under UK law. However, your rights will always depend on the specific facts and any mandatory legal protections that apply.',
        },
      ],
    },
    {
      title: '6. If the Host cancels the Event',
      content: [
        {
          type: 'text',
          text: 'If the Host cancels a Paid Event, Careerbook may automatically issue a full refund to affected Attendees, including:',
        },
        {
          type: 'bulleted',
          items: ['the full ticket price, course fee, or other Event price; and', 'the full Careerbook Booking Fee.'],
        },
        {
          type: 'text',
          text: 'Refunds will usually be returned to the original payment method used for the booking. Careerbook does not normally pay compensation for additional losses such as travel, accommodation, time, inconvenience, or other consequential costs.',
        },
      ],
    },
    {
      title: '7. Event changes and no paid-event rescheduling through the platform',
      content: [
        {
          type: 'text',
          text: "Careerbook does not currently support rescheduling of Paid Events through the platform. If a Host changes, postpones, materially amends, or cannot deliver a Paid Event, your rights will depend on the Host's terms and any applicable law. For event-specific concerns, including joining links, speaker changes, content changes, timetable changes, or delivery issues, you should contact the Host first.",
        },
      ],
    },
    {
      title: '8. Contacting the Host',
      content: [
        {
          type: 'text',
          text: 'If you have concerns or questions about:',
        },
        {
          type: 'bulleted',
          items: [
            'the Event content;',
            'access details;',
            'event quality;',
            'suitability;',
            'cancellation or postponement;',
            'refund eligibility;',
            'certificates, attendance, or joining instructions;',
          ],
        },
        {
          type: 'text',
          text: 'you must contact the Host in the first instance. Careerbook may assist administratively in some cases, but Careerbook is not generally responsible for resolving event delivery issues and is not the primary provider of the Event.',
        },
      ],
    },
    {
      title: '9. Use of refunded or cancelled bookings',
      content: [
        {
          type: 'text',
          text: 'If a booking is refunded or cancelled, you must not use the refunded ticket, access link, confirmation, or any copy of it to attend or access the Event.',
        },
      ],
    },
    {
      title: "10. Careerbook's role and liability",
      content: [
        {
          type: 'text',
          text: '10.1 Platform only\nCareerbook provides the booking platform and related tools but, unless expressly stated otherwise, does not organise, own, or deliver the Event.',
        },
        {
          type: 'text',
          text: '10.2 Not responsible for host conduct or event delivery\nTo the fullest extent permitted by law, Careerbook is not responsible for:',
        },
        {
          type: 'bulleted',
          items: [
            "the Host's delivery of the Event;",
            'the quality, safety, legality, or suitability of the Event;',
            "the Host's cancellation or refusal of a refund outside its published Refund Policy;",
            'speaker, venue, timetable, or content issues;',
            'user travel, accommodation, opportunity, or other consequential losses.',
          ],
        },
        {
          type: 'text',
          text: '10.3 Non-excludable rights preserved\nNothing in these Terms excludes any rights you have under mandatory consumer law or any liability that cannot lawfully be excluded.',
        },
        {
          type: 'text',
          text: "10.4 Liability cap\nTo the fullest extent permitted by law, Careerbook's total aggregate liability arising out of or in connection with your use of the booking platform shall not exceed the amount of the Booking Fee actually retained by Careerbook for the relevant booking, or GBP 100 if greater. Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, fraudulent misrepresentation, or any matter which cannot lawfully be limited.",
        },
      ],
    },
    {
      title: '11. Chargebacks and payment disputes',
      content: [
        {
          type: 'text',
          text: "If you believe a payment was taken incorrectly or the booked service was not provided, you may also have rights through your card issuer or payment provider, including chargeback rights in some cases. Any such process is subject to your card issuer's or payment provider's rules and is not controlled by Careerbook.",
        },
      ],
    },
    {
      title: '12. Data and communications',
      content: [
        {
          type: 'text',
          text: 'Careerbook and the Host may send you communications relating to your booking, including confirmations, updates, joining instructions, refunds, event notices, and service communications. Your use of Careerbook is subject to our Privacy Policy and Cookie Policy.',
        },
      ],
    },
    {
      title: '13. Governing law and jurisdiction',
      content: [
        {
          type: 'text',
          text: 'These Terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction over disputes arising from or connected with these Terms, except where mandatory consumer law allows you to bring a claim elsewhere in the UK.',
        },
      ],
    },
    {
      title: '14. Contact',
      content: [
        { type: 'text', text: 'For platform questions, contact:' },
        { type: 'text', text: 'Careerbook Enterprise Ltd' },
        { type: 'text', text: 'Website: www.careerbook.co.uk' },
        { type: 'text', text: 'Email: info@careerbook.co.uk' },
        { type: 'text', text: 'Address: 5 Wingate Close, St-Leonards-On-Sea, Sussex, TN38 0SD' },
      ],
    },
  ],
};
